import React, { useState, useEffect } from 'react';
import { useInput, useRecordContext, useNotify } from 'react-admin';

interface AutoCropFileInputProps {
  source: string;
  label?: string;
  [key: string]: any;
}

export const AutoCropFileInput = ({ source, label, ...props }: AutoCropFileInputProps) => {
  const { field } = useInput({ source });
  const record = useRecordContext();
  const notify = useNotify();
  const [preview, setPreview] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [userRemoved, setUserRemoved] = useState(false);

  useEffect(() => {
    if (record && record.cover_image_url && !preview && !userRemoved) {
      setPreview(record.cover_image_url);
    }

  }, [record, userRemoved]);


  const removeImage = () => {

    if (preview && preview.startsWith('blob:')) {
      URL.revokeObjectURL(preview);
    }
    
    // Устанавливаем флаг что пользователь удалил картинку
    setUserRemoved(true);
    
    // Принудительно очищаем состояние
    setPreview(null);

    field.onChange(null);

    const input = document.getElementById(`${source}-input`) as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;


    setUserRemoved(false);


    const maxSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    
    if (file.size > maxSize) {
      notify('Image size must be less than 5MB', { type: 'warning' });
      return;
    }
    
    if (!allowedTypes.includes(file.type)) {
      notify('Only JPG, PNG and WebP images are allowed', { type: 'warning' });
      return;
    }

    setIsProcessing(true);

    try {

      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        const size = Math.min(img.width, img.height);
        

        canvas.width = size;
        canvas.height = size;
        const x = (img.width - size) / 2;
        const y = (img.height - size) / 2;

        ctx?.drawImage(img, x, y, size, size, 0, 0, size, size);

            canvas.toBlob((blob) => {
              if (blob) {
                const croppedFile = new File([blob], file.name, { type: 'image/jpeg' });

                const previewUrl = URL.createObjectURL(blob);
                setPreview(previewUrl);
                field.onChange({
                  rawFile: croppedFile,
                  src: previewUrl
                });
              }
              setIsProcessing(false);
            }, 'image/jpeg', 0.9);
      };
      
      img.src = URL.createObjectURL(file);
    } catch (error) {
      console.error('Error processing image:', error instanceof Error ? error.message : 'Unknown error');
      notify('Error processing image. Please try again.', { type: 'error' });
      setIsProcessing(false);
    }
  };

  return (
    <div>
      <div>
        <label 
          htmlFor={`${source}-input`}
          style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}
        >
          {preview ? 'Replace cover image:' : `${label || 'Cover Image'}:`}
        </label>
        <input
          id={`${source}-input`}
          type="file"
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={handleFileChange}
          style={{ marginBottom: '16px' }}
          disabled={isProcessing}
        />
      </div>
      
      {isProcessing && (
        <div style={{ marginBottom: '8px', color: '#666' }}>
          Processing image...
        </div>
      )}
      
      {preview && (
        <div style={{ marginTop: '8px', position: 'relative', display: 'inline-block' }}>
          <img 
            src={preview} 
            alt="Cover preview" 
            style={{ 
              width: '150px', 
              height: '150px', 
              objectFit: 'cover', 
              borderRadius: '8px',
              border: '2px solid #ddd'
            }} 
          />
          <button
            onClick={removeImage}
            style={{
              position: 'absolute',
              top: '-8px',
              right: '-8px',
              background: '#f44336',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              width: '24px',
              height: '24px',
              cursor: 'pointer',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}
            title="Remove cover image"
          >
            ×
          </button>
          <div style={{ marginTop: '4px', fontSize: '12px', color: '#666' }}>
            {field.value && field.value.rawFile ? 'Auto-cropped to square' : 'Current cover image'}
          </div>
        </div>
      )}
    </div>
  );
};

